import { Link } from "react-router-dom";
import { FolderKanban, ScrollText, Image as ImageIcon, BookOpen, BarChart3 } from "lucide-react";
import { WorkflowNSWRABenefits } from "./WorkflowEPARScope";

/* Workflow presentation — EPAR Finalisation
 *
 * Mirrors the EPAR Scope + EW Finalisation walkthroughs. EPAR Finalisation
 * reuses the same /inbox/<job> assessor workflow already in production; this
 * page is the NSWRA-facing storyboard of how the tool handles the
 * delivery-phase reconciliation (final cost vs approved scope, completion
 * photos, council sign-off).
 */
export function WorkflowEPARFinalisation() {
  const steps = [
    {
      n: 1, title: "Ingest the EPAR Finalisation package",
      sub: "Delivery-phase ZIP from SmartyGrants or OneGMS",
      body: "Council uploads the EPAR Finalisation package — typically containing the approved EPAR Scope as a baseline plus the actual delivery evidence (final invoices, completion photos, variation notes, certifying engineer sign-off, BIM/GIS as-built data). RAAgent auto-detects portal type at 95-100% confidence, unpacks nested completion-evidence ZIPs in the background and classifies every file against the NSWRA Cat B Finalisation file-structure spec.",
      tech: "Async ingest · nested ZIP extraction · Cat B Finalisation spec compliance · 95-100% portal confidence",
    },
    {
      n: 2, title: "Final cost vs approved scope — the core reconciliation",
      sub: "Per-DM comparison: what was approved at scope, what's claimed at finalisation",
      body: "For every Damage Memo, RAAgent surfaces the approved Form 2 ERC from the EPAR Scope assessment alongside the council's final claimed amount. Variances are auto-flagged: under-spend (return to NSWRA), over-spend within approved tolerance (assessor reviews), over-spend outside tolerance (variation required, triggers RFI). Line-item drilldown shows where the variance landed (more labour, different methodology, scope creep) so the assessor can make a defensible call.",
      tech: "Approved-Form-2 lookup · variance buckets (under / in-tolerance / over) · per-line drift analysis · auto-RFI suggestion when variation evidence missing",
    },
    {
      n: 3, title: "Completion evidence — damage and completion side-by-side",
      sub: "Photo strip is damage + completion (not pre + damage)",
      body: "EPAR Finalisation evidence focuses on substantial completion, not pre-disaster condition. RAAgent re-buckets the photo strip per-DM: damage (what was broken) and completion (what was built). The assessor walks each DM, eyeballing the like-for-like restoration. EXIF/GPS/timestamp verification flags any photo that fails the chain-of-custody checks (no GPS, no timestamp, photo predates the disaster).",
      tech: "Per-DM damage/completion bucketing · EXIF + GPS + timestamp chain-of-custody · self-healing re-walk on first job open",
    },
    {
      n: 4, title: "Ledger reconciliation against approved CET",
      sub: "Actuals vs Form 2 verified ERC — the State only pays what's eligible",
      body: "Same Ledger Checker engine as EW Finalisation: every line auto-classified Eligible / Ineligible / Review against the NSWRA DRFA rules, submitted on-costs stripped, on-costs re-applied at the published Fact Sheet rate against eligible labour only. The assessor gets full per-line override (ELG / INEL / REV verdict, on-cost tri-state toggle, free-text override reason). The total reconciles back to the approved Form 2 ERC — variance highlighted in real time.",
      tech: "iter_rows streaming parser (15k rows in 1.8s) · Fact Sheet on-cost re-application · per-line override + audit trail · live variance vs approved Form 2 ERC",
    },
    {
      n: 5, title: "D / C / S sign-off — the assessor's three certifications",
      sub: "Damage / Costs / Substantial-completion, plus YES / RFI / NO per DM",
      body: "Each DM row carries three tick-box certifications: Damage (the restoration matches the approved scope), Costs (the actuals reconcile to the approved ERC within tolerance), and Substantial-completion (the council's engineer has certified delivery). Plus the standard YES / RFI / NO verdict and free-text method. The progress chip flips green only when all three certifications + the verdict are populated for every DM.",
      tech: "Per-DM D/C/S certification capture · readiness chip · audit-trail timestamps · works alongside the standard per-DM verdict",
    },
    {
      n: 6, title: "Finalisation Assessment Report + Verified Package output",
      sub: "Closes the loop with NSWRA and the council",
      body: "RAAgent emits two artefacts: (a) the EPAR Finalisation Assessment Report .xlsx — fills the official NSWRA SQP Assessment Record template with the verified delivery numbers, variance notes and assessor sign-off; (b) the Verified Package ZIP in the exact NSWRA Cat B Finalisation folder structure (Final Cost Estimation Evidence / Verified Completion Evidence / Council Engineer Certification / Assessor Correspondence). Ready to drop into SharePoint and push back to SmartyGrants.",
      tech: "SQP Assessment Record template population · openpyxl write_only mode · Cat B Finalisation folder spec · ready for SharePoint sync",
    },
  ];
  return (
    <div className="space-y-8" data-testid="workflow-epar-finalisation-page">
      <div className="border-b border-slate-200 pb-6">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-purple-700 mb-2">Workflow walkthrough</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">EPAR Finalisation</h1>
        <p className="text-base text-slate-600 mt-2 max-w-3xl leading-relaxed">
          How RAAgent reconciles a council&apos;s actual EPAR delivery against the approved EPAR Scope —
          per-DM variance analysis, completion-photo verification, D/C/S sign-off — and produces both
          the Assessment Report and the Verified Package output ready for SharePoint.
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-[12px]">
          <Link to="/portal/EPAR%20Finalisation" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-700" data-testid="workflow-epar-final-cta-portal">
            <FolderKanban className="w-3.5 h-3.5"/> Open EPAR Finalisation portal
          </Link>
          <Link to="/workflow/epar-scope" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <BookOpen className="w-3.5 h-3.5"/> EPAR Scope workflow
          </Link>
          <Link to="/workflow/ew-finalisation" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <BookOpen className="w-3.5 h-3.5"/> EW Finalisation workflow
          </Link>
          <Link to="/tools/cet-verifier" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <BarChart3 className="w-3.5 h-3.5"/> Try the CET Verifier
          </Link>
          <Link to="/tools/photo-checker" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <ImageIcon className="w-3.5 h-3.5"/> Try the Photo Checker
          </Link>
          <Link to="/tools/ledger-checker" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <ScrollText className="w-3.5 h-3.5"/> Try the Ledger Checker
          </Link>
        </div>
      </div>

      <WorkflowNSWRABenefits />

      <div className="space-y-5">
        {steps.map((s) => (
          <div key={s.n} className="card p-6 flex gap-5" data-testid={`workflow-epar-final-step-${s.n}`}>
            <div className="shrink-0 w-14 h-14 rounded-full bg-purple-600 text-white flex items-center justify-center font-heading font-bold text-2xl">
              {s.n}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-0.5">Step {s.n}</div>
              <h2 className="font-heading text-xl font-semibold leading-tight">{s.title}</h2>
              <div className="text-sm text-slate-500 italic mt-0.5">{s.sub}</div>
              <p className="text-[13.5px] text-slate-700 mt-3 leading-relaxed">{s.body}</p>
              <div className="mt-3 text-[11px] text-slate-500 font-mono border-l-2 border-slate-200 pl-3">
                {s.tech}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-6 bg-purple-50 border-purple-200">
        <h3 className="font-heading text-lg font-semibold mb-2">Why finalisation matters — closing the loop</h3>
        <p className="text-[13px] text-slate-700 leading-relaxed">
          EPAR Scope approval sets the maximum the State will pay. EPAR Finalisation reconciles that
          envelope against what the council actually delivered. Without a rigorous finalisation step,
          the State pays approved-scope dollars for under-delivered works, and over-runs without
          variation evidence flow through unchallenged. RAAgent makes the reconciliation explicit,
          per-DM and per-line, with the assessor signing off the three certifications (Damage,
          Costs, Substantial-completion) — defensible by design.
        </p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
          {[
            "Under-spend → return to NSWRA",
            "In tolerance → assessor confirms",
            "Over-spend, evidenced variation → assessor accepts",
            "Over-spend, no variation → automatic RFI",
            "Scope drift → variation review trigger",
            "Substantial completion not certified → automatic RFI",
          ].map((s) => (
            <div key={s} className="bg-white border border-purple-200 rounded p-2 font-mono">{s}</div>
          ))}
        </div>
        <div className="text-[10.5px] text-slate-500 mt-2">Variance buckets per NSW RA EPAR Finalisation Guidelines + Standard Treatment Guide v2.1.</div>
      </div>
    </div>
  );
}
